<template>
  <div class="estadisticas-dashboard">
    
    <!-- Header -->
    <div class="manager-header">
      <div class="row align-items-center">
        <div class="col-md-8">
          <h4 class="manager-title">
            <i class="nc-icon nc-chart-bar-32 mr-2"></i>
            Estadísticas Dashboard
          </h4>
          <small class="text-muted">
            Métricas y estadísticas detalladas de familias, subfamilias y planes
          </small>
        </div>
        <div class="col-md-4 text-right">
          <button 
            class="btn btn-primary btn-sm mr-2" 
            @click="cargarEstadisticas"
            :disabled="loading"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-refresh-02 mr-1"></i>
            Actualizar
          </button>
          <button 
            class="btn btn-outline-info btn-sm" 
            @click="exportarReporte"
            :disabled="loading || !(estadisticasCompletas && estadisticasCompletas.totales)"
          >
            <i class="nc-icon nc-paper mr-1"></i>
            Exportar
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Global -->
    <div v-if="loading && !(estadisticasCompletas && estadisticasCompletas.totales)" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando estadísticas...</span>
      </div>
      <p class="mt-3 text-muted">Generando análisis completo del catálogo...</p>
    </div>

    <!-- Contenido Principal -->
    <div v-else>
      
      <!-- Estadísticas Básicas -->
      <div class="stats-overview mb-4">
        <div class="row">
          <div class="col-md-3 col-6 mb-3">
            <div class="stat-card families">
              <div class="stat-content">
                <div class="stat-icon">
                  <i class="nc-icon nc-folder-17"></i>
                </div>
                <div class="stat-info">
                  <h3>{{ (estadisticasCompletas.totales && estadisticasCompletas.totales.familias) || 0 }}</h3>
                  <p>Familias</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-3 col-6 mb-3">
            <div class="stat-card subfamilies">
              <div class="stat-content">
                <div class="stat-icon">
                  <i class="nc-icon nc-folder-17"></i>
                </div>
                <div class="stat-info">
                  <h3>{{ (estadisticasCompletas.totales && estadisticasCompletas.totales.subfamilias) || 0 }}</h3>
                  <p>SubFamilias</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-3 col-6 mb-3">
            <div class="stat-card plans">
              <div class="stat-content">
                <div class="stat-icon">
                  <i class="nc-icon nc-credit-card"></i>
                </div>
                <div class="stat-info">
                  <h3>{{ (estadisticasCompletas.totales && estadisticasCompletas.totales.planes) || 0 }}</h3>
                  <p>Planes Totales</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-3 col-6 mb-3">
            <div class="stat-card active-plans">
              <div class="stat-content">
                <div class="stat-icon">
                  <i class="nc-icon nc-check-2"></i>
                </div>
                <div class="stat-info">
                  <h3>{{ (estadisticasCompletas.totales && estadisticasCompletas.totales.planesActivos) || 0 }}</h3>
                  <p>Planes Activos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Métricas Avanzadas -->
      <div class="metrics-section mb-4">
        <div class="row">
          <div class="col-md-3 col-6 mb-3">
            <div class="metric-card price">
              <div class="metric-header">
                <i class="nc-icon nc-money-coins"></i>
                <h6>Valor Promedio</h6>
              </div>
              <div class="metric-value">
                ${{ formatearPrecio((estadisticasCompletas.metricas && estadisticasCompletas.metricas.valorPromedio) || 0) }}
              </div>
              <div class="metric-range">
                ${{ formatearPrecio((estadisticasCompletas.metricas && estadisticasCompletas.metricas.valorMinimo) || 0) }} - 
                ${{ formatearPrecio((estadisticasCompletas.metricas && estadisticasCompletas.metricas.valorMaximo) || 0) }}
              </div>
            </div>
          </div>

          <div class="col-md-3 col-6 mb-3">
            <div class="metric-card duration">
              <div class="metric-header">
                <i class="nc-icon nc-time-alarm"></i>
                <h6>Duración Promedio</h6>
              </div>
              <div class="metric-value">
                {{ ((estadisticasCompletas.metricas && estadisticasCompletas.metricas.duracionPromedio) || 0).toFixed(1) }} meses
              </div>
              <div class="metric-subtitle">
                Tiempo promedio de planes
              </div>
            </div>
          </div>

          <div class="col-md-3 col-6 mb-3">
            <div class="metric-card sales">
              <div class="metric-header">
                <i class="nc-icon nc-cart-simple"></i>
                <h6>Efectividad Venta</h6>
              </div>
              <div class="metric-value">
                {{ (estadisticasCompletas.resumen && estadisticasCompletas.resumen.efectividadVenta) || 0 }}%
              </div>
              <div class="metric-subtitle">
                {{ (estadisticasCompletas.totales && estadisticasCompletas.totales.planesVenta) || 0 }} de {{ (estadisticasCompletas.totales && estadisticasCompletas.totales.planes) || 0 }} disponibles
              </div>
            </div>
          </div>

          <div class="col-md-3 col-6 mb-3">
            <div class="metric-card growth">
              <div class="metric-header">
                <i class="nc-icon nc-chart-pie-36"></i>
                <h6>Tendencia</h6>
              </div>
              <div class="metric-value" :class="getTendenciaClass()">
                <i :class="getTendenciaIcon()"></i>
                {{ getTendenciaTexto() }}
              </div>
              <div class="metric-subtitle">
                Análisis de crecimiento
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Análisis de Salud del Catálogo -->
      <div v-if="estadisticasCompletas.analisis" class="health-analysis mb-4">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">
              <i class="nc-icon nc-diamond mr-2"></i>
              Análisis de Salud del Catálogo
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3 col-6 mb-3" v-for="(valor, indicador) in estadisticasCompletas.analisis.indicadores" :key="indicador">
                <div class="health-indicator">
                  <div class="indicator-icon" :class="getHealthClass(valor)">
                    <i :class="getHealthIcon(valor)"></i>
                  </div>
                  <div class="indicator-info">
                    <h6>{{ formatearIndicador(indicador) }}</h6>
                    <span class="indicator-value" :class="getHealthClass(valor)">{{ formatearValorIndicador(valor) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Distribución y Análisis -->
      <div class="charts-section mb-4">
        <div class="row">
          
          <!-- Distribución por Familia -->
          <div class="col-md-6 mb-4">
            <div class="card">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="nc-icon nc-chart-pie-36 mr-2"></i>
                  Distribución por Familia
                </h6>
              </div>
              <div class="card-body">
                <div v-if="estadisticasCompletas.distribucion && estadisticasCompletas.distribucion.porFamilia && estadisticasCompletas.distribucion.porFamilia.length > 0" class="familia-distribution">
                  <div v-for="familia in estadisticasCompletas.distribucion.porFamilia.slice(0, 5)" :key="familia._id" class="familia-item">
                    <div class="familia-info">
                      <strong>{{ familia.nombreFamilia }}</strong>
                      <small>{{ familia.cantidad }} plan{{ familia.cantidad > 1 ? 'es' : '' }}</small>
                    </div>
                    <div class="familia-metrics">
                      <span class="plan-count">{{ familia.cantidad }}</span>
                      <span class="average-price">${{ formatearPrecio(familia.valorPromedio) }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center text-muted">
                  <i class="nc-icon nc-chart-pie-36" style="font-size: 2rem;"></i>
                  <p class="mt-2">No hay datos de distribución</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Distribución por Rango de Precio -->
          <div class="col-md-6 mb-4">
            <div class="card">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="nc-icon nc-money-coins mr-2"></i>
                  Distribución por Precios
                </h6>
              </div>
              <div class="card-body">
                <div v-if="estadisticasCompletas.distribucion && estadisticasCompletas.distribucion.porRangoPrecio && estadisticasCompletas.distribucion.porRangoPrecio.length > 0" class="price-distribution">
                  <div v-for="rango in estadisticasCompletas.distribucion.porRangoPrecio" :key="rango.rango" class="price-item">
                    <div class="price-info">
                      <strong>{{ rango.rango }}</strong>
                      <small>{{ rango.cantidad }} plan{{ rango.cantidad > 1 ? 'es' : '' }}</small>
                    </div>
                    <div class="price-metrics">
                      <span class="plan-count">{{ rango.cantidad }}</span>
                      <span class="average-price">${{ formatearPrecio(rango.valorPromedio) }}</span>
                    </div>
                    <div class="price-bar">
                      <div class="bar-fill" :style="{ width: getPorcentajeRango(rango.cantidad) + '%' }"></div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center text-muted">
                  <i class="nc-icon nc-money-coins" style="font-size: 2rem;"></i>
                  <p class="mt-2">No hay datos de precios</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Planes Populares -->
      <div v-if="estadisticasCompletas.tendencias && estadisticasCompletas.tendencias.planesPopulares && estadisticasCompletas.tendencias.planesPopulares.length > 0" class="popular-plans mb-4">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">
              <i class="nc-icon nc-favourite-28 mr-2"></i>
              Planes Más Populares
              <span class="badge badge-primary ml-2">Top {{ estadisticasCompletas.tendencias.planesPopulares.length }}</span>
            </h6>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th width="60">Rank</th>
                    <th>Plan</th>
                    <th>Familia / SubFamilia</th>
                    <th>Precio</th>
                    <th width="100">Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(plan, index) in estadisticasCompletas.tendencias.planesPopulares.slice(0, 8)" :key="plan._id">
                    <td>
                      <span class="rank-badge" :class="getRankClass(index)">
                        {{ index + 1 }}
                      </span>
                    </td>
                    <td>
                      <div class="plan-info">
                        <strong>{{ plan.descripcion }}</strong>
                        <br>
                        <small class="text-muted">ID: {{ plan.id }}</small>
                      </div>
                    </td>
                    <td>
                      <div class="family-info">
                        <div>{{ plan.nombreFamilia }}</div>
                        <small class="text-muted">{{ plan.nombreSubFamilia }}</small>
                      </div>
                    </td>
                    <td>
                      <span class="price-value">${{ formatearPrecio(plan.valor) }}</span>
                    </td>
                    <td>
                      <div class="score-container">
                        <span class="score-value">{{ plan.scorePopularidad }}</span>
                        <div class="score-bar">
                          <div class="score-fill" :style="{ width: plan.scorePopularidad + '%' }"></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Recomendaciones y Alertas -->
      <div v-if="tieneRecomendacionesOAlertas()" class="recommendations-alerts mb-4">
        <div class="row">
          
          <!-- Recomendaciones -->
          <div v-if="estadisticasCompletas.analisis && estadisticasCompletas.analisis.recomendaciones && estadisticasCompletas.analisis.recomendaciones.length > 0" class="col-md-6 mb-4">
            <div class="card recommendations-card">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="nc-icon nc-bulb-63 mr-2"></i>
                  Recomendaciones
                  <span class="badge badge-info ml-2">{{ estadisticasCompletas.analisis.recomendaciones.length }}</span>
                </h6>
              </div>
              <div class="card-body">
                <div v-for="rec in estadisticasCompletas.analisis.recomendaciones" :key="rec.titulo" class="recommendation-item">
                  <div class="rec-header">
                    <span class="rec-priority" :class="getPriorityClass(rec.prioridad)">
                      {{ formatearPrioridad(rec.prioridad) }}
                    </span>
                    <h6>{{ rec.titulo }}</h6>
                  </div>
                  <p class="rec-description">{{ rec.descripcion }}</p>
                  <div v-if="rec.metrica" class="rec-metric">
                    <small class="text-muted">{{ rec.metrica }}</small>
                  </div>
                  <div v-if="rec.objetivo" class="rec-objective">
                    <small><strong>Objetivo:</strong> {{ rec.objetivo }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Alertas -->
          <div v-if="estadisticasCompletas.analisis && estadisticasCompletas.analisis.alertas && estadisticasCompletas.analisis.alertas.length > 0" class="col-md-6 mb-4">
            <div class="card alerts-card">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="nc-icon nc-bell-55 mr-2"></i>
                  Alertas
                  <span class="badge badge-warning ml-2">{{ estadisticasCompletas.analisis.alertas.length }}</span>
                </h6>
              </div>
              <div class="card-body">
                <div v-for="alerta in estadisticasCompletas.analisis.alertas" :key="alerta.titulo" class="alert-item">
                  <div class="alert-header">
                    <span class="alert-priority" :class="getPriorityClass(alerta.prioridad)">
                      {{ formatearPrioridad(alerta.prioridad) }}
                    </span>
                    <h6>{{ alerta.titulo }}</h6>
                  </div>
                  <p class="alert-description">{{ alerta.descripcion }}</p>
                  <div v-if="alerta.metrica" class="alert-metric">
                    <small class="text-muted">{{ alerta.metrica }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Oportunidades -->
          <div v-if="estadisticasCompletas.analisis && estadisticasCompletas.analisis.oportunidades && estadisticasCompletas.analisis.oportunidades.length > 0" class="col-md-12 mb-4">
            <div class="card opportunities-card">
              <div class="card-header">
                <h6 class="card-title mb-0">
                  <i class="nc-icon nc-spaceship mr-2"></i>
                  Oportunidades de Mejora
                  <span class="badge badge-success ml-2">{{ estadisticasCompletas.analisis.oportunidades.length }}</span>
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div v-for="oportunidad in estadisticasCompletas.analisis.oportunidades" :key="oportunidad.titulo" class="col-md-6 mb-3">
                    <div class="opportunity-item">
                      <div class="opp-header">
                        <span class="opp-priority" :class="getPriorityClass(oportunidad.prioridad)">
                          {{ formatearPrioridad(oportunidad.prioridad) }}
                        </span>
                        <h6>{{ oportunidad.titulo }}</h6>
                      </div>
                      <p class="opp-description">{{ oportunidad.descripcion }}</p>
                      <div v-if="oportunidad.metrica" class="opp-metric">
                        <small class="text-muted">{{ oportunidad.metrica }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Información de Generación -->
      <div v-if="estadisticasCompletas.metadata" class="metadata-section">
        <div class="card">
          <div class="card-body py-3">
            <div class="row align-items-center">
              <div class="col-md-8">
                <small class="text-muted">
                  <i class="nc-icon nc-time-alarm mr-1"></i>
                  Generado el {{ formatearFechaCompleta(estadisticasCompletas.metadata.fechaGeneracion) }}
                  por {{ estadisticasCompletas.metadata.usuarioConsulta }}
                </small>
              </div>
              <div class="col-md-4 text-right">
                <small class="text-muted">
                  Versión {{ estadisticasCompletas.metadata.versionAnalisis }} | 
                  {{ estadisticasCompletas.metadata.empresaConsulta }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
export default {
  name: 'EstadisticasDashboard',
  props: {
    estadisticas: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      estadisticasCompletas: {}
    }
  },
  async mounted() {
    await this.cargarEstadisticas()
  },
  methods: {
    async cargarEstadisticas() {
      try {
        this.loading = true
        
        console.log('📊 Cargando estadísticas completas de planes...')
        
        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('jCxbraoQeD')}`
          },
          data: {
            Header: {
              Operacion: '/v1/planes/estadisticas'
            },
            Auth: {
              Token: sessionStorage.getItem('jCxbraoQeD')
            },
            Parameters: {},
            Body: {}
          }
        })

        console.log('📊 Respuesta estadísticas:', response.data)

        if (response.data && response.data.dataOut) {
          this.estadisticasCompletas = response.data.dataOut
          
          // Emitir estadísticas básicas al componente padre
          this.$emit('refresh')
          
          console.log('✅ Estadísticas cargadas exitosamente')
          
          this.$swal.fire({
            title: '¡Análisis Completado!',
            text: 'Las estadísticas del catálogo han sido generadas exitosamente',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          })
          
        } else {
          throw new Error('No se encontraron datos de estadísticas')
        }
        
      } catch (error) {
        console.error('❌ Error al cargar estadísticas:', error)
        
        let errorMessage = 'No se pudieron cargar las estadísticas'
        
        if (error.response && error.response.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response && error.response.status === 500) {
          errorMessage = 'Error interno del servidor. Inténtalo nuevamente.'
        }
        
        this.$swal.fire({
          title: 'Error al Cargar Estadísticas',
          text: errorMessage,
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 4000,
          showConfirmButton: false
        })
        
      } finally {
        this.loading = false
      }
    },

    exportarReporte() {
      this.$swal.fire({
        title: 'Próximamente',
        text: 'La exportación de reportes estará disponible en una próxima actualización',
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonClass: 'btn btn-primary'
      })
    },

    // Formateo y utilidades
    formatearPrecio(valor) {
      if (!valor) return '0'
      return new Intl.NumberFormat('es-CL').format(valor)
    },

    formatearFechaCompleta(fecha) {
      if (!fecha) return ''
      return new Date(fecha).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatearIndicador(indicador) {
      const nombres = {
        saludCatalogo: 'Salud del Catálogo',
        diversidadPrecios: 'Diversidad de Precios', 
        distribucionFamilias: 'Distribución Familias',
        actividadReciente: 'Actividad Reciente'
      }
      return nombres[indicador] || indicador
    },

    formatearValorIndicador(valor) {
      const traducciones = {
        excelente: 'Excelente',
        bueno: 'Bueno',
        regular: 'Regular',
        malo: 'Malo',
        alta: 'Alta',
        media: 'Media',
        baja: 'Baja',
        equilibrada: 'Equilibrada',
        concentrada: 'Concentrada',
        dispersa: 'Dispersa',
        activo: 'Activo',
        inactivo: 'Inactivo'
      }
      return traducciones[valor] || valor
    },

    formatearPrioridad(prioridad) {
      const traducciones = {
        alta: 'Alta',
        media: 'Media',
        baja: 'Baja'
      }
      return traducciones[prioridad] || prioridad
    },

    // Clases CSS dinámicas
    getTendenciaClass() {
      const tendencia = this.estadisticasCompletas.resumen && this.estadisticasCompletas.resumen.tendenciaCrecimiento
      return {
        'text-success': tendencia === 'positivo',
        'text-warning': tendencia === 'neutral',
        'text-danger': tendencia === 'negativo'
      }
    },

    getTendenciaIcon() {
      const tendencia = this.estadisticasCompletas.resumen && this.estadisticasCompletas.resumen.tendenciaCrecimiento
      return {
        'nc-icon nc-minimal-up': tendencia === 'positivo',
        'nc-icon nc-minimal-right': tendencia === 'neutral', 
        'nc-icon nc-minimal-down': tendencia === 'negativo'
      }
    },

    getTendenciaTexto() {
      const tendencia = this.estadisticasCompletas.resumen && this.estadisticasCompletas.resumen.tendenciaCrecimiento
      const traducciones = {
        positivo: 'Positiva',
        neutral: 'Estable',
        negativo: 'Negativa'
      }
      return traducciones[tendencia] || 'N/A'
    },

    getHealthClass(valor) {
      if (['excelente', 'alta', 'equilibrada', 'activo'].includes(valor)) {
        return 'health-excellent'
      } else if (['bueno', 'media'].includes(valor)) {
        return 'health-good'
      } else if (['regular', 'baja'].includes(valor)) {
        return 'health-regular'
      } else {
        return 'health-poor'
      }
    },

    getHealthIcon(valor) {
      if (['excelente', 'alta', 'equilibrada', 'activo'].includes(valor)) {
        return 'nc-icon nc-check-2'
      } else if (['bueno', 'media'].includes(valor)) {
        return 'nc-icon nc-satisfied'
      } else if (['regular', 'baja'].includes(valor)) {
        return 'nc-icon nc-circle-10'
      } else {
        return 'nc-icon nc-simple-remove'
      }
    },

    getPriorityClass(prioridad) {
      return {
        'priority-high': prioridad === 'alta',
        'priority-medium': prioridad === 'media',
        'priority-low': prioridad === 'baja'
      }
    },

    getRankClass(index) {
      if (index === 0) return 'rank-gold'
      if (index === 1) return 'rank-silver'
      if (index === 2) return 'rank-bronze'
      return 'rank-default'
    },

    getPorcentajeRango(cantidad) {
      const total = (this.estadisticasCompletas.totales && this.estadisticasCompletas.totales.planes) || 1
      return Math.round((cantidad / total) * 100)
    },

    tieneRecomendacionesOAlertas() {
      const analisis = this.estadisticasCompletas.analisis
      return analisis && (
        (analisis.recomendaciones && analisis.recomendaciones.length > 0) ||
        (analisis.alertas && analisis.alertas.length > 0) ||
        (analisis.oportunidades && analisis.oportunidades.length > 0)
      )
    }
  }
}
</script>

<style scoped>
.estadisticas-dashboard {
  padding: 20px;
}

.manager-header {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.manager-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

/* Estadísticas Básicas */
.stats-overview .stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

.stats-overview .stat-card:hover {
  transform: translateY(-5px);
}

.stat-content {
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 1.5rem;
  color: white;
}

.stat-info h3 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
  color: #2c3e50;
}

.stat-info p {
  margin: 0;
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;
}

.stat-card.families .stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-card.subfamilies .stat-icon {
  background: linear-gradient(135deg, #17a2b8 0%, #1e7e34 100%);
}

.stat-card.plans .stat-icon {
  background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
}

.stat-card.active-plans .stat-icon {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

/* Métricas Avanzadas */
.metrics-section .metric-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease;
}

.metrics-section .metric-card:hover {
  transform: translateY(-3px);
}

.metric-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.metric-header i {
  font-size: 1.2rem;
  margin-right: 8px;
  color: #667eea;
}

.metric-header h6 {
  margin: 0;
  font-weight: 600;
  color: #2c3e50;
}

.metric-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
}

.metric-range,
.metric-subtitle {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

/* 
 * ANÁLISIS DE SALUD DEL CATÁLOGO
 * Indicadores visuales optimizados con:
 * - Íconos blancos sobre fondo de color (máximo contraste)
 * - Texto de estado con fondo suave y bordes (legibilidad mejorada)
 * - Tamaños de fuente reducidos para mejor proporción
 * - Espaciado compacto y efectos hover responsive
 */
.health-analysis .card {
  border: none;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  border-radius: 12px;
  overflow: hidden;
}

.health-analysis .card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 20px;
}

.health-analysis .card-header h6 {
  color: white;
  margin-bottom: 0;
  font-weight: 600;
  font-size: 1rem;
}

.health-analysis .card-header i {
  color: white;
  opacity: 0.9;
}

.health-analysis .card-body {
  padding: 20px 15px;
  background: #fafbfc;
}

.health-indicator {
  display: flex;
  align-items: center;
  padding: 15px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.health-indicator:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}

.indicator-icon {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: white !important;
  font-size: 1.1rem;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.indicator-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.health-indicator:hover .indicator-icon::before {
  left: 100%;
}

.indicator-info {
  flex: 1;
}

.indicator-info h6 {
  margin: 0 0 6px 0;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.85rem;
  line-height: 1.2;
}

.indicator-value {
  font-weight: 600;
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 15px;
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Clases de salud para íconos (fondo de color) */
.health-excellent.indicator-icon {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white !important;
}

.health-good.indicator-icon {
  background: linear-gradient(135deg, #17a2b8 0%, #20c997 100%);
  color: white !important;
}

.health-regular.indicator-icon {
  background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
  color: white !important;
}

.health-poor.indicator-icon {
  background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
  color: white !important;
}

/* Clases de salud para texto (color de texto y fondo suave) */
.health-excellent.indicator-value {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.health-good.indicator-value {
  background: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
  border: 1px solid rgba(23, 162, 184, 0.2);
}

.health-regular.indicator-value {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.health-poor.indicator-value {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.2);
}

/* Responsive para indicadores de salud */
@media (max-width: 768px) {
  .health-analysis .card-header {
    text-align: center;
    padding: 12px 15px;
  }
  
  .health-analysis .card-body {
    padding: 20px 15px;
  }
  
  .health-indicator {
    padding: 12px;
    flex-direction: column;
    text-align: center;
  }
  
  .indicator-icon {
    width: 45px;
    height: 45px;
    margin-right: 0;
    margin-bottom: 12px;
    font-size: 1.2rem;
  }
  
  .indicator-info h6 {
    font-size: 0.8rem;
    margin-bottom: 5px;
  }
  
  .indicator-value {
    font-size: 0.7rem;
    padding: 3px 8px;
  }
}

@media (max-width: 576px) {
  .health-analysis .card-header {
    padding: 10px 12px;
  }
  
  .health-analysis .card-header h6 {
    font-size: 0.85rem;
  }
  
  .health-analysis .card-body {
    padding: 15px 12px;
  }
  
  .health-indicator {
    padding: 10px;
  }
  
  .indicator-icon {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    margin-bottom: 10px;
  }
  
  .indicator-info h6 {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }
  
  .indicator-value {
    font-size: 0.65rem;
    padding: 3px 6px;
    letter-spacing: 0.2px;
  }
}

/* Distribuciones */
.charts-section .card {
  border: none;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.familia-distribution,
.price-distribution {
  max-height: 300px;
  overflow-y: auto;
}

.familia-item,
.price-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.familia-item:last-child,
.price-item:last-child {
  border-bottom: none;
}

.familia-info,
.price-info {
  flex: 1;
}

.familia-info strong,
.price-info strong {
  font-weight: 600;
  color: #2c3e50;
  display: block;
}

.familia-info small,
.price-info small {
  color: #6c757d;
  font-size: 0.8rem;
}

.familia-metrics,
.price-metrics {
  display: flex;
  align-items: center;
  gap: 15px;
}

.plan-count {
  background: #667eea;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.average-price {
  font-weight: 600;
  color: #28a745;
  font-size: 0.9rem;
}

.price-bar {
  width: 100%;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.5s ease;
}

/* Planes Populares */
.popular-plans .card {
  border: none;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
}

.rank-gold {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #856404;
}

.rank-silver {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  color: #495057;
}

.rank-bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #deb887 100%);
  color: #6f4e37;
}

.rank-default {
  background: #6c757d;
}

.plan-info strong {
  color: #2c3e50;
  font-weight: 600;
}

.family-info div {
  font-weight: 600;
  color: #2c3e50;
}

.price-value {
  font-weight: 700;
  color: #28a745;
  font-size: 1.1rem;
}

.score-container {
  text-align: center;
}

.score-value {
  font-weight: 700;
  color: #2c3e50;
  display: block;
  margin-bottom: 5px;
}

.score-bar {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.score-fill {
  height: 100%;
  background: linear-gradient(90deg, #28a745 0%, #20c997 100%);
  transition: width 0.5s ease;
}

/* Recomendaciones y Alertas */
.recommendations-card {
  border-left: 4px solid #17a2b8;
}

.alerts-card {
  border-left: 4px solid #ffc107;
}

.opportunities-card {
  border-left: 4px solid #28a745;
}

.recommendation-item,
.alert-item,
.opportunity-item {
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.recommendation-item:last-child,
.alert-item:last-child,
.opportunity-item:last-child {
  border-bottom: none;
}

.rec-header,
.alert-header,
.opp-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.rec-priority,
.alert-priority,
.opp-priority {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-right: 10px;
  text-transform: uppercase;
}

.priority-high {
  background: #dc3545;
  color: white;
}

.priority-medium {
  background: #ffc107;
  color: #212529;
}

.priority-low {
  background: #28a745;
  color: white;
}

.rec-description,
.alert-description,
.opp-description {
  margin-bottom: 8px;
  color: #495057;
  line-height: 1.5;
}

.rec-metric,
.alert-metric,
.opp-metric,
.rec-objective {
  margin-bottom: 5px;
}

/* Metadata */
.metadata-section .card {
  border: none;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  background: #f8f9fa;
}

/* Responsive */
@media (max-width: 768px) {
  .estadisticas-dashboard {
    padding: 10px;
  }
  
  .manager-header {
    padding: 15px;
    text-align: center;
  }
  
  .stat-content {
    flex-direction: column;
    text-align: center;
  }
  
  .stat-icon {
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .familia-metrics,
  .price-metrics {
    flex-direction: column;
    gap: 5px;
  }
  
  .health-indicator {
    flex-direction: column;
    text-align: center;
  }
  
  .indicator-icon {
    margin-right: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: 576px) {
  .manager-header .row {
    text-align: center;
  }
  
  .manager-header .col-md-4 {
    margin-top: 15px;
  }
  
  .familia-item,
  .price-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .familia-metrics,
  .price-metrics {
    width: 100%;
    justify-content: space-between;
  }
}
</style> 